import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Warning } from "@phosphor-icons/react";
import api, { apiErr } from "../api";
import { FounderShell, RangeBar, MetricCard, DistBars, MetricValue } from "../components/founder";

const Panel = ({ title, subtitle, children, testid }) => (
  <div className="ff-panel rounded-md p-5" data-testid={testid}>
    <h2 className="text-lg font-semibold text-ff-gold">{title}</h2>
    {subtitle && <p className="mt-0.5 text-xs text-ff-muted">{subtitle}</p>}
    <div className="mt-3">{children}</div>
  </div>
);

export default function FounderAnalytics() {
  const [range, setRange] = useState("last_30_days");
  const [tz, setTz] = useState("America/New_York");
  const [d, setD] = useState(null);
  const [rd, setRd] = useState(null);

  const load = useCallback(() => {
    setD(null);
    api.get(`/founder/analytics?range=${range}&tz=${encodeURIComponent(tz)}`)
      .then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [range, tz]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/founder/replay-diversity").then((r) => setRd(r.data)).catch(() => {});
  }, []);

  return (
    <FounderShell>
      <div className="space-y-4" data-testid="founder-analytics-page">
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Deep Analytics</h1>
        <RangeBar range={range} setRange={setRange} tz={tz} setTz={setTz} meta={d?.range && { label: d.range.label, generated_at: d.generated_at }} />
        {!d ? <div className="ff-panel rounded-md p-8 text-center text-ff-muted">Computing analytics…</div> : (
          <div className="space-y-4">
            <Panel title="Gameplay Funnel" subtitle="Server-derived stages; event-based stages accrue over time" testid="an-funnel">
              {d.funnel.largest_dropoff?.stage && (
                <div className="mb-3 flex items-center gap-2 rounded-sm border border-ff-gold/30 bg-ff-gold/5 p-2 text-sm text-ff-gold" data-testid="funnel-dropoff">
                  <Warning size={16} weight="duotone" /> Largest drop-off: <b>{d.funnel.largest_dropoff.stage}</b> (−{d.funnel.largest_dropoff.drop}%)
                </div>
              )}
              <div className="space-y-1.5">
                {d.funnel.stages.map((s) => (
                  <div key={s.key} className="flex items-center justify-between border-b border-ff-brass/10 py-1.5 text-sm" data-testid={`funnel-stage-${s.key}`}>
                    <span className="text-ff-muted">{s.label}</span>
                    <span className="flex items-center gap-3">
                      <span className="text-ff-parchment">{s.status === "ok" ? s.count?.toLocaleString() : <span className="text-xs italic text-ff-muted">{s.status === "insufficient_data" ? "Insufficient data" : "Unavailable"}</span>}</span>
                      {s.conversion_pct != null && <span className="w-14 text-right text-ff-brass">{s.conversion_pct}%</span>}
                    </span>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="Retention Cohorts" subtitle="Cohort size shown beside each rate; small cohorts suppressed" testid="an-retention">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                {Object.entries(d.retention.overall).map(([band, m]) => (
                  <div key={band} className="rounded-md border border-ff-brass/20 bg-ff-bg2/60 p-3 text-center" data-testid={`retention-${band}`}>
                    <div className="text-[11px] uppercase tracking-widest text-ff-brass">{band}</div>
                    <div className="font-display text-xl font-bold text-ff-gold"><MetricValue metric={m} suffix="%" /></div>
                    <div className="text-[11px] text-ff-muted">n={m.cohort ?? 0}</div>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="Expedition Analytics" testid="an-expeditions">
              <div className="space-y-2">
                {d.expeditions.expeditions.map((e) => (
                  <div key={e.expedition_id} className="rounded-md border border-ff-brass/20 bg-ff-bg2/50 p-3" data-testid={`exp-${e.expedition_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold capitalize text-ff-parchment">{e.expedition_id.replace(/_/g, " ")}</span>
                      <span className="text-sm text-ff-gold"><MetricValue metric={e.completion_rate} suffix="%" /></span>
                    </div>
                    <div className="mt-1 grid grid-cols-2 gap-x-4 text-xs text-ff-muted sm:grid-cols-4">
                      <span>Starts: {e.starts}</span><span>Completed: {e.completions}</span>
                      <span>Avg stake: <MetricValue metric={e.avg_stake} /></span>
                      <span>Median time: <MetricValue metric={e.median_completion_sec} suffix="s" /></span>
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-ff-muted">{d.expeditions.commissioned_note}</p>
              </div>
            </Panel>

            <div className="grid gap-4 md:grid-cols-2">
              <Panel title="Virtual-Gold Economy" testid="an-economy">
                <div className="grid grid-cols-2 gap-3">
                  <MetricCard label="In Circulation" metric={d.economy.gold_in_circulation} testid="eco-circ" />
                  <MetricCard label="Created" metric={d.economy.gold_created} testid="eco-created" />
                  <MetricCard label="Removed" metric={d.economy.gold_removed} testid="eco-removed" />
                  <MetricCard label="Median Balance" metric={d.economy.median_balance} testid="eco-median" />
                  <MetricCard label="Rollbacks" metric={d.economy.compensating_rollbacks} testid="eco-rollback" />
                  <MetricCard label="Neg-Balance Attempts" metric={d.economy.negative_balance_attempts} testid="eco-neg" />
                </div>
                {(d.economy.alerts || []).map((a, i) => (
                  <div key={i} className="mt-2 rounded-sm border border-ff-gold/30 bg-ff-gold/5 p-2 text-xs text-ff-gold" data-testid={`eco-alert-${i}`}>{a.text}</div>
                ))}
              </Panel>
              <Panel title="Prestige & Progression" testid="an-progression">
                <DistBars dist={d.progression.prestige_tier_distribution} testid="prog-tier-bars" />
                <div className="mt-3 text-sm text-ff-muted">Singular titles: <span className="text-ff-parchment"><MetricValue metric={d.progression.singular_titles_held} /></span> · Historic firsts: <span className="text-ff-parchment"><MetricValue metric={d.progression.historic_firsts} /></span></div>
              </Panel>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Panel title="Crew Analytics" subtitle={d.crew.note} testid="an-crew">
                <div className="space-y-1.5">
                  {d.crew.crew.slice(0, 8).map((c) => (
                    <div key={c.crew_id} className="flex items-center justify-between text-sm" data-testid={`crew-${c.crew_id}`}>
                      <span className="text-ff-parchment">{c.name}</span>
                      <span className="text-xs text-ff-muted">holders {c.holders} · loyalty {c.avg_loyalty ?? "—"}</span>
                    </div>
                  ))}
                </div>
              </Panel>
              <Panel title="Rival & Social" testid="an-social">
                <div className="grid grid-cols-2 gap-3">
                  <MetricCard label="Following Rivals" metric={d.social.players_following_rivals} testid="soc-following" />
                  <MetricCard label="Avg Rivals" metric={d.social.avg_rivals_followed} testid="soc-avg" />
                  <MetricCard label="Total Follows" metric={d.social.total_follows} testid="soc-follows" />
                  <MetricCard label="Overtakes" metric={d.social.overtake_events} testid="soc-overtakes" />
                </div>
              </Panel>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Panel title="Daily Expeditions" testid="an-daily">
                <div className="grid grid-cols-2 gap-3">
                  <MetricCard label="Ranked" metric={d.daily.ranked_attempts} testid="daily-ranked" />
                  <MetricCard label="Practice" metric={d.daily.practice_attempts} testid="daily-practice" />
                  <MetricCard label="Completion" metric={d.daily.completion_rate} suffix="%" testid="daily-completion" />
                </div>
                <div className="mt-3"><DistBars dist={d.daily.streak_distribution} testid="daily-streaks" /></div>
              </Panel>
              <Panel title="Explorer Journey (preview)" subtitle={d.journey.disclaimer} testid="an-journey">
                <DistBars dist={d.journey.stage_distribution} testid="journey-bars" />
              </Panel>
            </div>

            <Panel title="Sponsor Readiness" testid="an-sponsor">
              <div className="mb-3 rounded-sm border border-ff-gold/30 bg-ff-gold/5 p-2.5 text-sm text-ff-gold" data-testid="sponsor-notice">{d.sponsor_readiness.notice}</div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <MetricCard label="Active Eligible" metric={d.sponsor_readiness.active_eligible_players} testid="spr-active" />
                <MetricCard label="Completion Rate" metric={d.sponsor_readiness.verified_completion_rate} suffix="%" testid="spr-comp" />
                <MetricCard label="Adult Players" metric={d.sponsor_readiness.eligible_adult_players} testid="spr-adult" />
                <MetricCard label="Commissioned" metric={d.sponsor_readiness.commissioned_state} testid="spr-commissioned" />
              </div>
            </Panel>

            {rd && (
              <Panel title="Replay Diversity" subtitle={rd.target} testid="an-replay-diversity">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs" data-testid="replay-diversity-table">
                    <thead className="text-ff-brass">
                      <tr>
                        <th className="p-2">Expedition</th><th className="p-2">Pool</th><th className="p-2">Shown</th>
                        <th className="p-2">Runs</th><th className="p-2">Avg overlap</th>
                        <th className="p-2">Seq repeat</th><th className="p-2">Wording repeat</th>
                        <th className="p-2">Order repeat</th><th className="p-2">Fallback</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rd.expeditions.map((e) => {
                        const pc = (v) => (v == null ? "—" : `${v}%`);
                        return (
                          <tr key={e.expedition_id} className="border-t border-ff-brass/10" data-testid={`rd-row-${e.expedition_id}`}>
                            <td className="p-2 text-ff-parchment">{e.expedition_id}</td>
                            <td className="p-2 text-ff-muted">{e.pool_size}</td>
                            <td className="p-2 text-ff-muted">{e.decisions_per_run}</td>
                            <td className="p-2 text-ff-muted">{e.runs_analyzed}</td>
                            <td className={`p-2 ${e.avg_decision_overlap_pct != null && e.avg_decision_overlap_pct <= 40 ? "text-emerald-300" : "text-ff-parchment"}`}>{pc(e.avg_decision_overlap_pct)}</td>
                            <td className="p-2 text-ff-muted">{pc(e.exact_decision_sequence_repeat_pct)}</td>
                            <td className="p-2 text-ff-muted">{pc(e.wording_variant_repeat_pct)}</td>
                            <td className="p-2 text-ff-muted">{pc(e.answer_order_repeat_pct)}</td>
                            <td className="p-2 text-ff-muted">{pc(e.pool_fallback_usage_pct)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Panel>
            )}
          </div>
        )}
      </div>
    </FounderShell>
  );
}
